import { useMutation, useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import FormLayout from 'features/App/components/FormLayout'
import { NewCoupon } from 'features/Coupons/components/NewCoupon'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { USER_LIMIT } from 'features/Coupons/components/UserLimit/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { CONFIRMATION_MODALITIES } from 'features/Services/components/ConfirmationModalities/constants'
import { getCouponValue } from 'resolvers/coupons'
import { resolvers as segmentsResolvers } from 'resolvers/segments'
import { getConfirmationModalitiesAsArray } from 'resolvers/services'
import { toLocalISOString } from 'utils/date'
import { canWrite } from 'features/Users/utils'
import { getDifference } from 'utils/object'
import { sanitizePrice } from 'utils/math'
import { isBothWays, getCitiesFromRespose } from 'features/BaseRules/utils'
import { GET_COUPON } from 'features/Campaigns/queries-legacy'
import { EDIT_COUPON } from 'features/Campaigns/mutations-legacy'
import { useLogin } from 'features/Login/hooks'
import { getCitySegmentsForMutation } from 'features/BaseRules/utils'
import { ICouponMappedResponse } from './types'
import { INewCouponForm } from 'features/Coupons/components/NewCoupon/types'
import {
  GTCampaignPlace,
  GTCoupon,
  GTLoyaltyCampaignPlace,
  GTLoyaltyCampaignRuleConditionsCitySegment
} from 'gql/types.generated'

const FormLayoutContent = styled(FormLayout.Content)`
  margin-bottom: 200px;
`

const messages = defineMessages({
  helmet: {
    id: 'coupons.edit.helmet',
    defaultMessage: 'Edit Coupon | Nexbus Admin'
  }
})

const mapCouponResponseToForm = (response: GTCoupon): ICouponMappedResponse => {
  const citySegments = response.constraints.citySegments ?? []
  const isAllSegments = citySegments.every((citySegment: TSFixMe): TSFixMe => citySegment.segments.length === 0)
  const isReturn = isBothWays(citySegments as GTLoyaltyCampaignRuleConditionsCitySegment[], isAllSegments)
  const cities = getCitiesFromRespose(citySegments as GTLoyaltyCampaignRuleConditionsCitySegment[]) as {
    origins: GTCampaignPlace[]
    destinations: GTCampaignPlace[]
  }
  return {
    ...response,
    constraints: {
      ...response.constraints,
      citySegments: {
        allCitiesAndSegments: isEmpty(citySegments),
        isAllSegments,
        destinations: cities.destinations,
        origins: cities.origins as GTCampaignPlace[],
        segments: citySegments.reduce((result: TSFixMe[], citySegment) => {
          return [
            ...result,
            ...citySegment.segments.map((segment) => {
              return {
                ...segment,
                cityOrigin: citySegment.origin,
                cityDestination: citySegment.destination
              }
            })
          ]
        }, []),
        isReturn
      },
      userLimit: response.constraints.userLimit === 1 ? USER_LIMIT.ONE_TIME : USER_LIMIT.MULTIPLE,
      confirmationModalities: getConfirmationModalitiesAsArray(response.constraints.confirmationModalities)
    }
  }
}
const EditCouponView = (): ReactElement => {
  const navigate = useNavigate()
  const { couponId } = useParams()
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(segmentsResolvers)
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])
  const hasAccess = canWrite(FEATURES.CAMPAIGN, login.accessToken.policies)

  let content

  const [editCoupon, { loading: mutationLoading, error: mutationError }] = useMutation(EDIT_COUPON, {
    onCompleted() {
      navigate('/coupons')
    }
  })

  const handleSubmit = (coupon: INewCouponForm): void => {
    const previousCoupon = {
      dates: {
        startsAt: queryData.coupon.startsAt,
        endsAt: queryData.coupon.endsAt
      },
      discount: {
        discountType: queryData.coupon.discountType,
        value: queryData.coupon.value
      },
      channels: queryData.coupon.channels
    }

    const newCoupon = {
      dates: {
        startsAt: toLocalISOString(new Date(coupon.startsAt)),
        endsAt: toLocalISOString(new Date(coupon.endsAt))
      },
      discount: {
        discountType: coupon.discountType,
        value: getCouponValue({ discountType: coupon.discountType, discountValue: coupon.value })
      },
      channels: coupon.channels
    }

    const payload = getDifference(newCoupon, previousCoupon) as TSFixMe

    if (coupon.constraints) {
      const citySegments = getCitySegmentsForMutation({
        allCitiesAndSegments: coupon.allCitiesAndSegments,
        allSegments: coupon.allSegments,
        cityOrigins: coupon.cityOrigins as GTLoyaltyCampaignPlace[],
        cityDestinations: coupon.cityDestinations as GTLoyaltyCampaignPlace[],
        segments: coupon.segments,
        isReturn: coupon.isReturn
      })
      payload.constraints = {
        minTicketsCount: !isEmpty(coupon.constraints.minTicketsCount)
          ? parseInt(coupon.constraints.minTicketsCount)
          : undefined,
        minSale: coupon.constraints.minSale ? sanitizePrice(Number(coupon.constraints.minSale) * 100, true) : 0,
        limit: parseInt(coupon.constraints.limit),
        userLimit: coupon.constraints.userLimit === USER_LIMIT.ONE_TIME ? 1 : parseInt(coupon.constraints.limit),
        citySegments,
        salesChannelIDs: coupon.constraints.salesChannels.map(({ id }) => id),
        departureDateRanges: coupon.constraints.departureDateRanges.map(({ from, to }: TSFixMe): TSFixMe => {
          return {
            from: toLocalISOString(from),
            to: toLocalISOString(to)
          }
        }),
        confirmationModalities: coupon.constraints.confirmationModalities,
        onlyRegisteredUsers: coupon.constraints.onlyRegisteredUsers,
        passengerTypes: coupon.passengerTypes.map(({ id }) => ({ id })),
        bookingTypes: coupon.constraints.bookingTypes
      }
    }

    if (payload.dates && (payload.dates.startsAt || payload.dates.endsAt)) {
      payload.dates.timezone = queryData.coupon.timezone
    }

    if (payload.constraints && payload.constraints.confirmationModalities) {
      payload.constraints.confirmationModalities = {
        [CONFIRMATION_MODALITIES.PAID]:
          payload.constraints.confirmationModalities.indexOf(CONFIRMATION_MODALITIES.PAID) > -1,
        [CONFIRMATION_MODALITIES.RESERVED]:
          payload.constraints.confirmationModalities.indexOf(CONFIRMATION_MODALITIES.RESERVED) > -1
      }
    }

    if (isEmpty(payload)) {
      navigate('/coupons')
    } else {
      editCoupon({
        variables: {
          campaignId: queryData.coupon.campaign.id,
          coupon: {
            id: couponId,
            ...payload
          }
        }
      })
    }
  }

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_COUPON, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !hasAccess,
    variables: {
      couponId
    }
  })

  useEffect(() => {
    if (hasAccess) {
      refetch()
    }
  }, [])

  if (!hasAccess) {
    return <ForbiddenView feature={FEATURES.CAMPAIGN} />
  } else if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError || !queryData || !queryData.coupon) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="coupons.edit.error.description"
              defaultMessage="There was an error while loading your coupon"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="coupons.edit.error.action" defaultMessage="Reload your coupon" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewCoupon
        onSubmit={handleSubmit}
        loading={mutationLoading}
        error={mutationError}
        coupon={mapCouponResponseToForm(queryData.coupon)}
        campaign={queryData.coupon.campaign}
        isEdit
      />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="coupons.edit.title" defaultMessage="Edit coupon tickets" />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayoutContent>
        <Card.Container>{content}</Card.Container>
      </FormLayoutContent>
    </div>
  )
}

export default withLastUrl(EditCouponView)
